import { useEffect, useRef, useState } from "react";
import { authAPI } from "../api";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise = null;
function loadGsiScript() {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", resolve);
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export default function GoogleSignInButton({ onSuccess, onError, label = "Continue with Google", disabled = false }) {
  const btnRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setUnavailable(true);
      return;
    }
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
          auto_select: false,
        });
        if (btnRef.current) {
          window.google.accounts.id.renderButton(btnRef.current, {
            theme: "outline",
            size: "large",
            text: "continue_with",
            shape: "pill",
            logo_alignment: "left",
            width: btnRef.current.offsetWidth || 320,
          });
        }
      })
      .catch(() => setUnavailable(true));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCredential(response) {
    if (!response.credential) {
      onError?.("Google sign-in was cancelled");
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.google(response.credential);
      onSuccess?.(res.user, res.token);
    } catch (e) {
      onError?.(e.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  }

  if (unavailable) {
    return (
      <div className="text-center text-xs text-gray-400 py-2 italic">
        {GOOGLE_CLIENT_ID
          ? "Google sign-in unavailable right now"
          : "Google sign-in not configured (set REACT_APP_GOOGLE_CLIENT_ID)"}
      </div>
    );
  }

  return (
    <div className="w-full">
      <div ref={btnRef} className={`flex justify-center ${disabled || loading ? "opacity-50 pointer-events-none" : ""}`} />
      {loading && <p className="text-xs text-center text-gray-500 mt-2">Signing in…</p>}
    </div>
  );
}
